import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronDown, Eye, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface HealthRecord {
  id: string;
  type: string;
  title: string;
  content?: string | null;
  text?: string | null;
  risk: string;
  ai_summary?: string | null;
  observed_at: string;
  metadata?: any;
  source_session_id?: string | null;
  document_id?: string | null;
}

interface RecordRowProps {
  record: HealthRecord;
  isOpen: boolean;
  onToggle: () => void;
  itemsCount?: number;
  onViewDocument?: (id: string, type: 'smartfile' | 'document') => void;
  onDelete?: () => void;
  sourceFileName?: string | null;
}

const TYPE_CONFIG: Record<string, { label: string; emoji: string; color: string }> = {
  anamnesis: { label: "Anamnese", emoji: "📋", color: "bg-blue-50 text-blue-700 border-blue-200" },
  anotacao: { label: "Anotação", emoji: "📝", color: "bg-gray-50 text-gray-700 border-gray-200" },
  consulta: { label: "Consulta", emoji: "🩺", color: "bg-purple-50 text-purple-700 border-purple-200" },
  vitals: { label: "Sinais Vitais", emoji: "❤️", color: "bg-red-50 text-red-700 border-red-200" },
  anthropometry: { label: "Antropometria", emoji: "📏", color: "bg-orange-50 text-orange-700 border-orange-200" },
  lab_exam: { label: "Exame Laboratorial", emoji: "🔬", color: "bg-teal-50 text-teal-700 border-teal-200" },
  exam: { label: "Exame", emoji: "🩺", color: "bg-green-50 text-green-700 border-green-200" },
  exame: { label: "Exame", emoji: "🩺", color: "bg-green-50 text-green-700 border-green-200" },
  results: { label: "Resultados", emoji: "📊", color: "bg-teal-50 text-teal-700 border-teal-200" },
  prescription: { label: "Prescrição", emoji: "💊", color: "bg-purple-50 text-purple-700 border-purple-200" },
  prescricao: { label: "Prescrição", emoji: "💊", color: "bg-purple-50 text-purple-700 border-purple-200" },
  report: { label: "Relatório", emoji: "📄", color: "bg-blue-50 text-blue-700 border-blue-200" },
  physiotherapy_evolution: { label: "Evolução Fisioterápica", emoji: "🏃", color: "bg-blue-50 text-blue-700 border-blue-200" },
  nutritional_assessment: { label: "Avaliação Nutricional", emoji: "🥗", color: "bg-green-50 text-green-700 border-green-200" },
  meal_plan: { label: "Plano Alimentar", emoji: "🍽️", color: "bg-lime-50 text-lime-700 border-lime-200" },
  training_plan: { label: "Plano de Treino", emoji: "💪", color: "bg-orange-50 text-orange-700 border-orange-200" },
  body_composition: { label: "Composição Corporal", emoji: "⚖️", color: "bg-purple-50 text-purple-700 border-purple-200" },
  psychotherapy_session: { label: "Sessão Psicoterapia", emoji: "🧠", color: "bg-pink-50 text-pink-700 border-pink-200" },
  aesthetic_protocol: { label: "Protocolo Estético", emoji: "✨", color: "bg-rose-50 text-rose-700 border-rose-200" },
  chiropractic_adjustment: { label: "Ajuste Quiroprático", emoji: "🦴", color: "bg-amber-50 text-amber-700 border-amber-200" },
  clinical_observation: { label: "Observação Clínica", emoji: "📋", color: "bg-blue-50 text-blue-700 border-blue-200" },
  clinical_note: { label: "Nota Clínica", emoji: "📋", color: "bg-blue-50 text-blue-700 border-blue-200" },
  other: { label: "Outro", emoji: "📝", color: "bg-gray-50 text-gray-700 border-gray-200" },
};

const RISK_BADGES: Record<string, string> = {
  critical: "bg-red-100 text-red-700 border-red-300",
  warn: "bg-yellow-100 text-yellow-700 border-yellow-300",
  info: "bg-gray-100 text-gray-600 border-gray-200",
};

export function RecordRow({ record, isOpen, onToggle, itemsCount, onViewDocument, onDelete, sourceFileName }: RecordRowProps) {
  const typeConfig = TYPE_CONFIG[record.type] || TYPE_CONFIG.other;
  const riskConfig = RISK_BADGES[record.risk] || RISK_BADGES.info;

  const formattedDate = format(new Date(record.observed_at), "dd/MM/yyyy", {
    locale: ptBR,
  });

  const sourceType = record.source_session_id ? 'smartfile' : record.document_id ? 'document' : 'manual';
  const displayFileName = sourceFileName || (sourceType === 'smartfile' ? 'Smartfile' : sourceType === 'document' ? 'Documento' : 'Manual');

  async function handleDelete() {
    try {
      // ✅ REMOVIDO: patient_health_records e health_record_items não são mais usados
      // Excluir apenas resultados laboratoriais relacionados
      await supabase.from("lab_results_onboarding").delete().match({ health_record_id: record.id });

      toast.success("✅ Registro excluído com sucesso!");
      onDelete?.();
    } catch (err: any) {
      console.error("Erro ao excluir:", err);
      toast.error("❌ Erro ao excluir registro");
    }
  }

  return (
    <Button
      variant="ghost"
      className="w-full h-auto p-0 hover:bg-muted/30"
      onClick={onToggle}
    >
      <div className={`w-full grid grid-cols-12 gap-2 px-4 py-3 text-left items-center ${isOpen ? 'bg-muted/10' : ''}`}>
        {/* Chevron */}
        <div className="col-span-1 flex items-center justify-center">
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>

        {/* Data */}
        <div className="col-span-3 md:col-span-2">
          <p className="text-sm font-medium">{formattedDate}</p>
        </div>

        {/* Tipo */}
        <div className="col-span-4 md:col-span-2">
          <Badge variant="outline" className={`${typeConfig.color} gap-1 text-xs`}>
            <span>{typeConfig.emoji}</span>
            <span className="hidden md:inline">{typeConfig.label}</span>
          </Badge>
        </div>

        {/* Título */}
        <div className="col-span-4 md:col-span-3">
          <div className="flex items-center gap-2">
            <p className="text-sm truncate">{record.title}</p>
            {itemsCount && itemsCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {itemsCount}
              </Badge>
            )}
          </div>
        </div>

        {/* Risco - Sempre exibir */}
        <div className="col-span-6 md:col-span-2 flex justify-end md:justify-start">
          <Badge variant="outline" className={`${riskConfig} text-xs`}>
            {record.risk === 'critical' && '🔴 Crítico'}
            {record.risk === 'warn' && '🟡 Atenção'}
            {record.risk === 'info' && '🟢 Normal'}
          </Badge>
        </div>

        {/* Origem - Clicável */}
        <div className="col-span-6 md:col-span-2 flex justify-end gap-1">
          {(record.source_session_id || record.document_id) ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                const docId = record.source_session_id || record.document_id;
                const docType = record.source_session_id ? 'smartfile' : 'document';
                onViewDocument?.(docId!, docType);
              }}
              className="h-7 px-2 gap-1"
            >
              <Badge variant="outline" className="text-xs gap-1 pointer-events-none max-w-[150px]">
                {sourceType === 'smartfile' && <span>🟣</span>}
                {sourceType === 'document' && <span>📄</span>}
                {sourceType === 'manual' && <span>✍️</span>}
                <span className="hidden md:inline truncate">{displayFileName}</span>
              </Badge>
              <Eye className="h-3 w-3" />
            </Button>
          ) : (
            <Badge variant="outline" className="text-xs gap-1">
              <span>✍️</span>
              <span className="hidden md:inline">Manual</span>
            </Badge>
          )}

          {/* Botão de exclusão */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => e.stopPropagation()}
                className="h-7 px-2 gap-1 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja excluir este registro?
                  Todos os dados associados (subregistros e resultados laboratoriais) serão removidos.
                  Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Sim, Excluir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </Button>
  );
}
